import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Save, Building2, DollarSign, Users, Package, Recycle, GraduationCap, MapPin, Briefcase, Plus, X } from 'lucide-react';
import HeroSection from './HeroSection';
import { useUser } from '../contexts/UserContext';

// Farm Profile Interfaces
interface FarmProfileData {
  farmName: string;
  farmType: 'broilers' | 'layers' | 'mixed';
  location: string;
  contactInfo: {
    phone: string;
    email: string;
    address: string;
  };
  revenueSources: RevenueSource[];
  customRevenueSources: CustomRevenueSource[];
}

interface RevenueSource {
  id: string;
  name: string;
  type: 'chicken_sales' | 'manure_sales' | 'training' | 'farm_visits' | 'consultancy' | 'others';
  enabled: boolean;
  description: string;
  estimatedValue: number;
  currency: string;
}

interface CustomRevenueSource {
  id: string;
  name: string;
  description: string;
  estimatedValue: number;
  currency: string;
  enabled: boolean;
}

const FARM_PROFILE_STORAGE_KEY = 'farm-profile-data';

// Default revenue sources
const DEFAULT_REVENUE_SOURCES: Omit<RevenueSource, 'id'>[] = [
  {
    name: 'Chicken Sales',
    type: 'chicken_sales',
    enabled: true,
    description: 'Sales of live chickens for meat production',
    estimatedValue: 15000,
    currency: 'UGX'
  },
  {
    name: 'Egg Sales',
    type: 'chicken_sales',
    enabled: false,
    description: 'Sales of fresh eggs from layer birds',
    estimatedValue: 200,
    currency: 'UGX'
  },
  {
    name: 'Manure Sales',
    type: 'manure_sales',
    enabled: true,
    description: 'Sales of poultry manure as organic fertilizer',
    estimatedValue: 5000,
    currency: 'UGX'
  },
  {
    name: 'Training Services',
    type: 'training',
    enabled: true,
    description: 'Poultry farming training and education services',
    estimatedValue: 50000,
    currency: 'UGX'
  },
  {
    name: 'Farm Visits',
    type: 'farm_visits',
    enabled: true,
    description: 'Educational farm tours and visits',
    estimatedValue: 25000,
    currency: 'UGX'
  },
  {
    name: 'Consultancy',
    type: 'consultancy',
    enabled: true,
    description: 'Poultry farming consultancy and advisory services',
    estimatedValue: 75000,
    currency: 'UGX'
  }
];

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  background: #f8fafc;
  color: #2d3748;
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #2d5016;
  margin: 0 0 10px 0;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin: 0 0 30px 0;
`;

const FormSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e0e0e0;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #2e7d32;
    box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #2e7d32;
    box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #2e7d32;
    box-shadow: 0 0 0 2px rgba(46, 125, 50, 0.1);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const RevenueSourceCard = styled.div<{ enabled: boolean }>`
  background: ${props => props.enabled ? '#f8f9fa' : '#ffffff'};
  border: 1px solid ${props => props.enabled ? '#2e7d32' : '#e0e0e0'};
  border-radius: 8px;
  padding: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #2e7d32;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RevenueSourceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const RevenueSourceIcon = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
`;

const RevenueSourceTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const RevenueSourceDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin: 0 0 12px 0;
`;


const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #2e7d32;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 14px 28px;
  border-radius: 25px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  ${props => props.variant === 'primary' ? `
    background: #2e7d32;
    color: white;
    &:hover { background: #1b5e20; transform: translateY(-1px); }
  ` : `
    background: #f5f5f5;
    color: #666;
    border: 1px solid #e0e0e0;
    &:hover { background: #e0e0e0; }
  `}
`;

const ActionBar = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
`;

const CustomRevenueSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
`;

const CustomRevenueItem = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DeleteButton = styled.button`
  background: #ffebee;
  color: #d32f2f;
  border: none;
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #ffcdd2;
  }
`;

const FarmProfile: React.FC = () => {
  const { userRole, setUserRole } = useUser();
  
  const [farmProfile, setFarmProfile] = useState<FarmProfileData>({
    farmName: '',
    farmType: 'mixed',
    location: '',
    contactInfo: {
      phone: '',
      email: '',
      address: ''
    },
    revenueSources: [],
    customRevenueSources: []
  });

  const [showAddCustom, setShowAddCustom] = useState(false);
  const [newCustomSource, setNewCustomSource] = useState({
    name: '',
    description: '',
    estimatedValue: 0,
    currency: 'UGX'
  });

  useEffect(() => {
    loadFarmProfile();
  }, []);

  const loadFarmProfile = () => {
    try {
      const saved = localStorage.getItem(FARM_PROFILE_STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        setFarmProfile(data);
      } else {
        // Initialize with default revenue sources
        const defaultProfile: FarmProfileData = {
          farmName: '',
          farmType: 'mixed',
          location: '',
          contactInfo: {
            phone: '',
            email: '',
            address: ''
          },
          revenueSources: DEFAULT_REVENUE_SOURCES.map(source => ({
            ...source,
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
          })),
          customRevenueSources: []
        };
        setFarmProfile(defaultProfile);
      }
    } catch (error) {
      console.error('Error loading farm profile:', error);
    }
  };

  const saveFarmProfile = () => {
    try {
      localStorage.setItem(FARM_PROFILE_STORAGE_KEY, JSON.stringify(farmProfile));
      // Farm profile saved successfully
    } catch (error) {
      console.error('Error saving farm profile:', error);
      // Error saving farm profile
    }
  };

  const handleRevenueSourceToggle = (sourceId: string) => {
    setFarmProfile(prev => ({
      ...prev,
      revenueSources: prev.revenueSources.map(source =>
        source.id === sourceId ? { ...source, enabled: !source.enabled } : source
      )
    }));
  };

  const handleCustomSourceAdd = () => {
    if (!newCustomSource.name.trim()) {
      // Custom revenue source name required
      return;
    }

    const customSource: CustomRevenueSource = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      ...newCustomSource,
      enabled: true
    };

    setFarmProfile(prev => ({
      ...prev,
      customRevenueSources: [...prev.customRevenueSources, customSource]
    }));

    setNewCustomSource({
      name: '',
      description: '',
      estimatedValue: 0,
      currency: 'UGX'
    });
    setShowAddCustom(false);
  };

  const handleCustomSourceDelete = (sourceId: string) => {
    setFarmProfile(prev => ({
      ...prev,
      customRevenueSources: prev.customRevenueSources.filter(source => source.id !== sourceId)
    }));
  };

  const getRevenueSourceIcon = (type: string) => {
    switch (type) {
      case 'chicken_sales':
        return <Package size={20} />;
      case 'manure_sales':
        return <Recycle size={20} />;
      case 'training':
        return <GraduationCap size={20} />;
      case 'farm_visits':
        return <MapPin size={20} />;
      case 'consultancy':
        return <Briefcase size={20} />;
      default:
        return <DollarSign size={20} />;
    }
  };

  const getRevenueSourceColor = (type: string) => {
    switch (type) {
      case 'chicken_sales':
        return '#2e7d32';
      case 'manure_sales':
        return '#795548';
      case 'training':
        return '#2196f3';
      case 'farm_visits':
        return '#ff9800';
      case 'consultancy':
        return '#9c27b0';
      default:
        return '#666';
    }
  };

  return (
    <>
      <HeroSection
        title="Farm Profile"
        subtitle="Configure your farm settings and revenue sources"
        description="Set up your farm profile, contact information, and customize revenue sources for accurate analytics"
      />
      <Container>
        <Title>Farm Profile</Title>
        <Subtitle>Configure your farm settings and revenue sources for accurate analytics</Subtitle>

        {/* User Role Management */}
        <FormSection>
          <SectionTitle>
            <Users size={24} />
            User Role & Permissions
          </SectionTitle>
          <Form>
            <Grid>
              <FormGroup>
                <Label>Current User Role</Label>
                <Select
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value as 'admin' | 'manager' | 'user')}
                >
                  <option value="admin">Administrator</option>
                  <option value="manager">Manager</option>
                  <option value="user">User</option>
                </Select>
                <p style={{ fontSize: '0.8rem', color: '#666', margin: '8px 0 0 0' }}>
                  {userRole === 'admin' && 'Full access to all features and data management'}
                  {userRole === 'manager' && 'Access to most features with limited administrative functions'}
                  {userRole === 'user' && 'Basic access to view and update farm data'}
                </p>
              </FormGroup>
            </Grid>
          </Form>
        </FormSection>

        {/* Basic Farm Information */}
        <FormSection>
          <SectionTitle>
            <Building2 size={24} />
            Basic Farm Information
          </SectionTitle>
          <Form>
            <Grid>
              <FormGroup>
                <Label>Farm Name</Label>
                <Input
                  type="text"
                  value={farmProfile.farmName}
                  onChange={(e) => setFarmProfile(prev => ({ ...prev, farmName: e.target.value }))}
                  placeholder="Enter your farm name"
                />
              </FormGroup>
              <FormGroup>
                <Label>Farm Type</Label>
                <Select
                  value={farmProfile.farmType}
                  onChange={(e) => setFarmProfile(prev => ({ ...prev, farmType: e.target.value as 'broilers' | 'layers' | 'mixed' }))}
                >
                  <option value="broilers">Broiler Farm</option>
                  <option value="layers">Layer Farm</option>
                  <option value="mixed">Mixed Farm</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Location</Label>
                <Input
                  type="text"
                  value={farmProfile.location}
                  onChange={(e) => setFarmProfile(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Enter farm location"
                />
              </FormGroup>
            </Grid>
          </Form>
        </FormSection>

        {/* Contact Information */}
        <FormSection>
          <SectionTitle>
            <Users size={24} />
            Contact Information
          </SectionTitle>
          <Form>
            <Grid>
              <FormGroup>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  value={farmProfile.contactInfo.phone}
                  onChange={(e) => setFarmProfile(prev => ({ 
                    ...prev, 
                    contactInfo: { ...prev.contactInfo, phone: e.target.value }
                  }))}
                  placeholder="Enter phone number"
                />
              </FormGroup>
              <FormGroup>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={farmProfile.contactInfo.email}
                  onChange={(e) => setFarmProfile(prev => ({ 
                    ...prev, 
                    contactInfo: { ...prev.contactInfo, email: e.target.value }
                  }))}
                  placeholder="Enter email address"
                />
              </FormGroup>
              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label>Address</Label>
                <TextArea
                  value={farmProfile.contactInfo.address}
                  onChange={(e) => setFarmProfile(prev => ({ 
                    ...prev, 
                    contactInfo: { ...prev.contactInfo, address: e.target.value }
                  }))}
                  placeholder="Enter complete farm address"
                />
              </FormGroup>
            </Grid>
          </Form>
        </FormSection>

        {/* Revenue Sources */}
        <FormSection>
          <SectionTitle>
            <DollarSign size={24} />
            Revenue Sources
          </SectionTitle>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Select the revenue sources that apply to your farm. This will be used for accurate analytics and reporting.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
            {farmProfile.revenueSources.map((source) => (
              <RevenueSourceCard
                key={source.id}
                enabled={source.enabled}
                onClick={() => handleRevenueSourceToggle(source.id)}
              >
                <RevenueSourceHeader>
                  <Checkbox
                    type="checkbox"
                    checked={source.enabled}
                    onChange={() => handleRevenueSourceToggle(source.id)}
                  />
                  <RevenueSourceIcon color={getRevenueSourceColor(source.type)}>
                    {getRevenueSourceIcon(source.type)}
                  </RevenueSourceIcon>
                  <RevenueSourceTitle style={{ fontSize: '0.9rem' }}>{source.name}</RevenueSourceTitle>
                </RevenueSourceHeader>
                <RevenueSourceDescription style={{ fontSize: '0.8rem', marginBottom: '8px' }}>
                  {source.description}
                </RevenueSourceDescription>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DollarSign size={14} />
                  <Input
                    type="number"
                    value={source.estimatedValue}
                    onChange={(e) => {
                      const newValue = parseFloat(e.target.value) || 0;
                      setFarmProfile(prev => ({
                        ...prev,
                        revenueSources: prev.revenueSources.map(s =>
                          s.id === source.id ? { ...s, estimatedValue: newValue } : s
                        )
                      }));
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ 
                      width: '80px', 
                      padding: '4px 8px', 
                      fontSize: '0.8rem',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px'
                    }}
                  />
                  <Select
                    value={source.currency}
                    onChange={(e) => {
                      setFarmProfile(prev => ({
                        ...prev,
                        revenueSources: prev.revenueSources.map(s =>
                          s.id === source.id ? { ...s, currency: e.target.value } : s
                        )
                      }));
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ 
                      padding: '4px 8px', 
                      fontSize: '0.8rem',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      minWidth: '60px'
                    }}
                  >
                    <option value="UGX">UGX</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="KES">KES</option>
                  </Select>
                </div>
              </RevenueSourceCard>
            ))}
          </div>

          {/* Custom Revenue Sources */}
          <CustomRevenueSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#333' }}>Custom Revenue Sources</h3>
              <Button
                variant="secondary"
                onClick={() => setShowAddCustom(true)}
              >
                <Plus size={16} />
                Add Custom Source
              </Button>
            </div>

            {farmProfile.customRevenueSources.map((source) => (
              <CustomRevenueItem key={source.id}>
                <Checkbox
                  type="checkbox"
                  checked={source.enabled}
                  onChange={() => setFarmProfile(prev => ({
                    ...prev,
                    customRevenueSources: prev.customRevenueSources.map(s =>
                      s.id === source.id ? { ...s, enabled: !s.enabled } : s
                    )
                  }))}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#333' }}>{source.name}</div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>{source.description}</div>
                  <div style={{ fontSize: '0.9rem', color: '#2e7d32', fontWeight: '500' }}>
                    {source.estimatedValue.toLocaleString()} {source.currency}
                  </div>
                </div>
                <DeleteButton onClick={() => handleCustomSourceDelete(source.id)}>
                  <X size={16} />
                </DeleteButton>
              </CustomRevenueItem>
            ))}

            {showAddCustom && (
              <div style={{ 
                background: '#f8f9fa', 
                border: '1px solid #e0e0e0', 
                borderRadius: '8px', 
                padding: '16px',
                marginTop: '12px'
              }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#333' }}>Add Custom Revenue Source</h4>
                <Grid>
                  <FormGroup>
                    <Label>Name</Label>
                    <Input
                      type="text"
                      value={newCustomSource.name}
                      onChange={(e) => setNewCustomSource(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter revenue source name"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Estimated Value</Label>
                    <Input
                      type="number"
                      value={newCustomSource.estimatedValue}
                      onChange={(e) => setNewCustomSource(prev => ({ ...prev, estimatedValue: parseFloat(e.target.value) || 0 }))}
                      placeholder="Enter estimated value"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Currency</Label>
                    <Select
                      value={newCustomSource.currency}
                      onChange={(e) => setNewCustomSource(prev => ({ ...prev, currency: e.target.value }))}
                    >
                      <option value="UGX">UGX</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="KES">KES</option>
                    </Select>
                  </FormGroup>
                  <FormGroup style={{ gridColumn: '1 / -1' }}>
                    <Label>Description</Label>
                    <TextArea
                      value={newCustomSource.description}
                      onChange={(e) => setNewCustomSource(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter description for this revenue source"
                    />
                  </FormGroup>
                </Grid>
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <Button variant="primary" onClick={handleCustomSourceAdd}>
                    <Save size={16} />
                    Add Source
                  </Button>
                  <Button variant="secondary" onClick={() => setShowAddCustom(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CustomRevenueSection>
        </FormSection>

        <ActionBar>
          <Button variant="primary" onClick={saveFarmProfile}>
            <Save size={20} />
            Save Farm Profile
          </Button>
        </ActionBar>
      </Container>
    </>
  );
};

export default FarmProfile;
